import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { fromEvent, tap } from 'rxjs';
import {
  type BufferGeometry,
  Color,
  DataTexture,
  FloatType,
  Matrix4,
  type NormalBufferAttributes,
  Points,
  Quaternion,
  RGBAFormat,
  type ShaderMaterial,
  Vector3,
} from 'three';

import { useAllObjectClassListQuery } from '@/hook';
import type { MaterialByViewMode } from '@/manager';
import { pcdManager } from '@/manager';
import { useSubscriptions } from '@/package';
import { usePcdRenderingStore } from '@/store';
import type { BoxWithLabel } from '@/type';
import { eulerToQuaternion } from '@/util';

import { useToolPcdCanvasContext } from './hook';
import useTargetLabelingBoxList from './hook/useTargetLabelingBoxList';

const ToolPcdCanvasPcdViewer = ({
  renderedIndex,
}: {
  renderedIndex: number;
}) => {
  const { data: allObjectClassList } = useAllObjectClassListQuery();
  const { backgroundPcd } = useToolPcdCanvasContext();
  const defaultPointCloud =
    useRef<Points<BufferGeometry<NormalBufferAttributes>, ShaderMaterial>>(
      null,
    );
  const pcdIndex = usePcdRenderingStore(state => state.pcdIndex);
  const renderedGeometryUuid = useRef('');
  const { isViewMode, viewMode } = useToolPcdCanvasContext();
  const targetLabelingBoxList = useTargetLabelingBoxList();
  const { scene } = useThree();
  const appliedShader = useRef(false);

  const applyShader = (
    geometry: (typeof pcdManager)['_geometry'],
    material: (typeof pcdManager)['_material'],
  ) => {
    if (!defaultPointCloud.current) return;

    setPointsPcdData(targetLabelingBoxList);

    defaultPointCloud.current.geometry.dispose();
    defaultPointCloud.current.material.dispose();

    defaultPointCloud.current.geometry = geometry;
    defaultPointCloud.current.material = material;

    appliedShader.current = true;
  };

  const reomvePointCloud = () => {
    if (!defaultPointCloud.current) return;

    defaultPointCloud.current.geometry.dispose();
    defaultPointCloud.current.material.dispose();

    scene.remove(defaultPointCloud.current);
  };
  const createPointCloud = () => {
    const geometry = pcdManager.getGeometry();
    const material = pcdManager.getMaterial();

    defaultPointCloud.current = new Points(
      geometry,
      backgroundPcd?.material ?? material,
    );
    defaultPointCloud.current.renderOrder = 0;
    defaultPointCloud.current.frustumCulled = false;

    scene.add(defaultPointCloud.current);

    renderedGeometryUuid.current = geometry.uuid;
  };

  const setPointsPcdData = (labelingBoxList: BoxWithLabel[]) => {
    const uBoxHalfSizeList: Vector3[] = [];
    const uBoxInverseMatrixList: Matrix4[] = [];
    const uBoxColorList: Color[] = [];
    const uNumActiveBoxList = labelingBoxList.length;
    const _viewMode = isViewMode.default
      ? undefined
      : (viewMode as keyof MaterialByViewMode);

    labelingBoxList
      .filter(v => v)
      .forEach(({ dimension, label, position, rotation }) => {
        uBoxHalfSizeList.push(new Vector3(...dimension.map(v => v / 2)));

        const color = new Color(
          allObjectClassList?.find(item => +item.id === +label)?.color ??
            '#ffffff',
        );

        // TODO: box 를 새롭게 추가하는 경우 rotation 이 null 로 오는 문제가 있음
        const quaternion = eulerToQuaternion(rotation ?? [0, 0, 0]);

        // 월드 변환 행렬을 생성하고 역행렬을 계산
        const worldMatrix = new Matrix4();
        const positionVec = new Vector3(...position);
        const quaternionObj = new Quaternion(...quaternion);
        const scale = new Vector3(1, 1, 1);

        // 박스의 월드 변환 행렬을 구성
        worldMatrix.compose(positionVec, quaternionObj, scale);

        // 역행렬을 계산 (World to Local 변환)
        uBoxInverseMatrixList.push(new Matrix4().copy(worldMatrix).invert());
        uBoxColorList.push(color);
      });

    // 행렬 데이터를 텍스처로 변환
    const textureData = new Float32Array(uBoxInverseMatrixList.length * 4 * 4);

    uBoxInverseMatrixList.forEach((m, i) => {
      m.elements.forEach((v, j) => {
        const dataIndex = i * 16 + j;

        textureData[dataIndex] = v;
      });
    });

    const textureWidth = 4;
    const textureHeight = Math.max(1, uBoxInverseMatrixList.length);

    const uMatrixTexture = new DataTexture(
      textureData,
      textureWidth,
      textureHeight,
      RGBAFormat,
      FloatType,
    );

    // NOTE: 없으면 모든 PCD 가 가장 마지막 ShaderMaterial 의 색상이 적용됨
    uMatrixTexture.needsUpdate = true;

    pcdManager.setMaterialUniform({
      viewMode: _viewMode,
      uniformMap: {
        uBoxColorList,
        uBoxHalfSizeList,
        uNumActiveBoxList,
        uMatrixTexture,
        fMatrixTextureHeight: textureHeight,
        bIsMainView: _viewMode == null,
      },
    });
  };

  useFrame(() => {
    if (!defaultPointCloud.current) return;

    const geometry = pcdManager.getGeometry();
    const material = isViewMode.default
      ? pcdManager.getMaterial()
      : pcdManager.getMaterial(viewMode as keyof MaterialByViewMode);

    if (geometry.uuid !== renderedGeometryUuid.current) {
      renderedGeometryUuid.current = geometry.uuid;
    }

    if (
      pcdIndex === renderedIndex &&
      geometry.uuid === renderedGeometryUuid.current &&
      !appliedShader.current
    ) {
      applyShader(geometry, material);
    }
  });

  useEffect(() => {
    createPointCloud();

    if (!isViewMode.default) {
      pcdManager.setMaterialUniform({
        viewMode: viewMode as keyof MaterialByViewMode,
        uniformMap: { bVisible: backgroundPcd.visible },
      });

      applyShader(
        pcdManager.getGeometry(),
        pcdManager.getMaterial(viewMode as keyof MaterialByViewMode),
      );
    }

    return reomvePointCloud;
  }, [backgroundPcd.visible, isViewMode, targetLabelingBoxList, viewMode]);

  useEffect(() => {
    if (!defaultPointCloud.current) return;

    setPointsPcdData(targetLabelingBoxList);
  }, [targetLabelingBoxList]);

  useSubscriptions(
    () => [
      fromEvent(pcdManager, 'sortedGeometry')
        .pipe(
          tap(() => {
            const geometry = pcdManager.getGeometry();
            const material = isViewMode.default
              ? pcdManager.getMaterial()
              : pcdManager.getMaterial(viewMode as keyof MaterialByViewMode);

            applyShader(geometry, material);
          }),
        )
        .subscribe(),
    ],
    [pcdIndex, targetLabelingBoxList, viewMode],
  );

  return <></>;
};

ToolPcdCanvasPcdViewer.displayName = 'ToolPcdCanvasPcdViewer';

export default ToolPcdCanvasPcdViewer;
